import anime from 'animejs/lib/anime.es';
import classNames from 'classnames';
import _ from 'lodash';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { isMobile } from 'react-device-detect';
import { createUseStyles } from 'react-jss';
import { useParams } from 'react-router-dom';
import { useOrderAPI } from '../../apis/order-api';
import whatsAppIcon from '../../assets/images/icon-whatsapp.png';
import { Buttons } from '../../components/bill/buttons';
import { Header } from '../../components/bill/header';
import { Orders } from '../../components/bill/orders';
import { Summary } from '../../components/bill/summary';
import { OAuthGoogleButton } from '../../components/auth/oauth-google-button';
import { useInterval } from '../../hooks/use-interval';
import { useOpenWhatsApp } from '../../hooks/use-open-whatsapp';
import { useSession } from '../../hooks/use-session';
import { useStorage } from '../../hooks/use-storage';
import { layoutDefaults } from '../../utils/layout-defaults';
import { parseContainerBackgroundImage } from '../../utils/manager/layout-manager';
import { useSpotManager } from '../../utils/manager/spot-manager';
import { useAnalytics } from '../../utils/setup/analytics-setup';
import { wait } from '../../utils/wait';
import { useCheckoutWorkflow } from '../../workflow/checkout-workflow';
import { useOrderCancelWorkflow } from '../../workflow/order-cancel-workflow';
import { NoBillScreen } from './no-bill-screen';
import { useSpotTransferModal } from './spot-transfer-modal';

const iconsAnimation =
  'animate__animated animate__fadeInRightBig animate__delay-1s';

function BillScreen() {
  const { menuSlug } = useParams();
  const { analyticsPageView } = useAnalytics();
  const {
    user,
    orders,
    setOrders,
    menuLayout,
    showingModal,
    config,
    payment,
    spot,
  } = useStorage();
  const [singleOrder, setSingleOrder] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [updating, setUpdating] = useState(true);
  const classes = useStyles(menuLayout);
  const showSpotTransferModal = useSpotTransferModal();
  const { prepareCheckoutWorkflow, showCheckoutWorkflow } =
    useCheckoutWorkflow();
  const openWhatsApp = useOpenWhatsApp();
  // const { prepareCancelOrderModal, showCancelOrderModal } =
  //   useCancelOrderModal();
  const { prepareOrderCancelWorkflow, showOrderCancelWorkflow } =
    useOrderCancelWorkflow();

  const { checkSpotChange } = useSpotManager();
  const { findSpotOrdersFromRemote } = useOrderAPI();
  const [cancelDisabled, setCancelDisabled] = useState(false);
  const containerRef = useRef();
  const ordersRef = useRef();
  const { prepaid } = spot ?? {};
  const checkAutoLoadCheckoutRef = useRef(false);

  useSession();
  useEffect(() => {
    if (orders?.length > 0) {
      analyticsPageView('Bill');
    }
  }, [orders?.length]);

  useEffect(() => {
    setSingleOrder((orders?.length ?? 0) === 1);
  }, [orders, user]);

  useEffect(() => {
    async function checkoutWorkflow() {
      await prepareCheckoutWorkflow({ selectedOrders, updateOrders });
      await showCheckoutWorkflow({ autoLoaded: true });
    }

    // if (payment?.status !== 'paying') {
    // }

    const checkAutoLoadCheckout = checkAutoLoadCheckoutRef.current;
    checkAutoLoadCheckoutRef.current = true;

    // if (payment?.status === 'paying' && !checkAutoLoadCheckoutRef.current) {
    if ((payment?.status === 'paying' || prepaid) && !checkAutoLoadCheckout) {
      checkoutWorkflow();
    }
    // }, [payment?.status, checkAutoLoadCheckoutRef]);
  }, [payment?.status, prepaid]);

  useLayoutEffect(() => {
    anime({
      targets: containerRef.current,
      opacity: showingModal ? 0.2 : 1,
      duration: showingModal ? 400 : 200,
      easing: 'easeInQuad',
    });
  }, [showingModal]);

  const onSelectInstructions = () => {
    setOrders(
      _.cloneDeep(orders).map((o) => {
        o.flashAt = new Date();
        return o;
      })
    );
  };

  const onWhatsAppClick = () => {
    openWhatsApp();
  };

  const onCancelOrder = async (order, callback) => {
    callback({ loading: true });
    setCancelDisabled(true);

    // let retry = false;
    // do {
    // eslint-disable-next-line no-await-in-loop
    await wait(200);
    setUpdating(false);

    // eslint-disable-next-line no-await-in-loop
    await prepareOrderCancelWorkflow({ order });
    // const result = await showOrderCancelWorkflow({
    // eslint-disable-next-line no-await-in-loop
    await showOrderCancelWorkflow({
      prepareOnPreConfirm: async () => {
        await updateOrders();
      },
    });

    // retry = result?.retry ?? false;

    // if (result?.order != null) {
    //   // eslint-disable-next-line no-await-in-loop
    //   await updateOrders();
    // }
    // } while (retry);

    setUpdating(true);

    setTimeout(() => {
      callback({ loading: false });
      setCancelDisabled(false);
    }, 400);
  };

  const updateOrders = useCallback(async () => {
    try {
      const response = await findSpotOrdersFromRemote();
      setOrders(response.data);

      await checkSpotChange(response.data, async (spotFromOrder) => {
        await showSpotTransferModal(spotFromOrder);
        analyticsPageView('Bill');
      });

      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        setOrders(null);
      } else {
        throw error;
      }
    }

    return null;
  }, [checkSpotChange, menuSlug, setOrders]);

  // useInterval(updateOrders, 5000, updating);
  useInterval(updateOrders, 10000, updating);

  return (
    <div ref={containerRef} className={classes.container}>
      <div className={classes.iconsContainer}>
        <div
          className={classNames(classes.icon, iconsAnimation)}
          style={{ '--animate-delay': '200ms' }}
          onClick={onWhatsAppClick}
          aria-hidden
        >
          <img src={whatsAppIcon} width="50" height="50" alt="" />
        </div>
      </div>

      <OAuthGoogleButton config={config} visible={false} />

      {_.isEmpty(orders) ? (
        <NoBillScreen />
      ) : (
        <>
          <Header singleOrder={singleOrder} />
          <Orders
            ref={ordersRef}
            singleOrder={singleOrder}
            selectedOrders={selectedOrders}
            cancelDisabled={cancelDisabled}
            onCancel={onCancelOrder}
            onSelectedOrdersChange={setSelectedOrders}
            onSelectInstructions={onSelectInstructions}
          />
          <Summary selectedOrders={selectedOrders} />
          <Buttons
            selectedOrders={selectedOrders}
            updateOrders={updateOrders}
            updating={updating}
            setUpdating={setUpdating}
            onSelectInstructions={onSelectInstructions}
          />
        </>
      )}
    </div>
  );
}

const useStyles = createUseStyles({
  container: {
    minHeight: '100vh',
    overflow: isMobile ? 'clip' : null,
    fontSize: layoutDefaults.fontSize,
    fontFamily: (layout) =>
      layout?.text?.default_font ?? layoutDefaults.fontFamily,
    backgroundImage: (layout) => parseContainerBackgroundImage(layout),

    paddingLeft: layoutDefaults.mainHorizontalMargin,
    paddingRight: layoutDefaults.mainHorizontalMargin,
    // border: '1px solid green',
  },
  iconsContainer: {
    position: 'fixed',
    top: 0,
    right: 0,
    opacity: 0.4,
    zIndex: 100,
    marginTop: `max(${layoutDefaults.mainHorizontalMargin}px, env(safe-area-inset-top))`,
    marginRight: layoutDefaults.mainHorizontalMargin,
    // border: '1px solid green',
  },
  icon: {
    marginLeft: layoutDefaults.smallerHorizontalMargin,
    cursor: 'pointer',
    // border: '1px solid red',
  },
});

export { BillScreen };
